import { useState, useCallback, useEffect } from 'react';
import { smbsApi } from '../../services/api';
import { CustomColumnDefinition, CustomColumnType } from '../../types/lead';
import { normalizeLabel } from '../../utils/leads/leadsUtils';
import { extractErrorMessage } from '../../utils/errorUtils';

export function useCustomColumns(smbId?: string) {
  const [customColumns, setCustomColumns] = useState<CustomColumnDefinition[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCustomColumns = useCallback(async () => {
    if (!smbId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await smbsApi.getCustomColumns(smbId);
      setCustomColumns(res.columns || []);
    } catch (err: any) {
      console.error('Failed to fetch columns:', err);
      setError(extractErrorMessage(err, 'Failed to load columns'));
    } finally {
      setLoading(false);
    }
  }, [smbId]);

  useEffect(() => {
    fetchCustomColumns();
  }, [fetchCustomColumns]);

  const saveCustomColumns = useCallback(
    async (updatedColumns: CustomColumnDefinition[]) => {
      if (!smbId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await smbsApi.updateCustomColumns(smbId, updatedColumns);
        setCustomColumns(res.columns || []);
        return res.columns;
      } catch (err: any) {
        console.error('Failed to save custom columns:', err);
        const msg = extractErrorMessage(err, 'Failed to update custom columns');
        setError(msg);
        throw new Error(msg);
      } finally {
        setLoading(false);
      }
    },
    [smbId]
  );

  const patchCustomColumn = useCallback(
    async (key: string, patchPayload: { name?: string; options?: string[] }) => {
      // Optimistically update customColumns state immediately
      setCustomColumns((prevCols) =>
        prevCols.map((col) => {
          if (col.key === key) {
            return {
              ...col,
              ...(patchPayload.name !== undefined ? { name: patchPayload.name } : {}),
              ...(patchPayload.options !== undefined ? { options: patchPayload.options } : {}),
            };
          }
          return col;
        })
      );

      if (smbId) {
        setLoading(true);
        setError(null);
        try {
          const res = await smbsApi.patchCustomColumn(smbId, key, patchPayload);
          if (res?.columns && Array.isArray(res.columns) && res.columns.length > 0) {
            setCustomColumns(res.columns);
            return res.columns;
          }
          return customColumns;
        } catch (err: any) {
          console.error('Failed to patch custom column:', err);
          fetchCustomColumns();
          const msg = extractErrorMessage(err, 'Failed to update custom column');
          setError(msg);
          throw new Error(msg);
        } finally {
          setLoading(false);
        }
      } else {
        const updated: CustomColumnDefinition[] = customColumns.map((col) => {
          if (col.key === key) {
            return {
              ...col,
              ...(patchPayload.name !== undefined ? { name: patchPayload.name } : {}),
              ...(patchPayload.options !== undefined ? { options: patchPayload.options } : {}),
            };
          }
          return col;
        });
        return await saveCustomColumns(updated);
      }
    },
    [smbId, customColumns, saveCustomColumns, fetchCustomColumns]
  );

  const createCustomColumn = useCallback(
    async (name: string, type: CustomColumnType, rawOptions?: string[]) => {
      if (!smbId) return;
      setLoading(true);
      setError(null);

      try {
        const res = await smbsApi.createCustomColumn(smbId, {
          name: name.trim(),
          type,
          options: rawOptions,
        });
        const updatedCols = res.columns || [];
        setCustomColumns(updatedCols);
        const newColumn = updatedCols[updatedCols.length - 1];
        return { columns: updatedCols, newColumn };
      } catch (err: any) {
        console.error('Failed to create custom column:', err);
        const msg = extractErrorMessage(err, 'Failed to create custom column');
        setError(msg);
        throw new Error(msg);
      } finally {
        setLoading(false);
      }
    },
    [smbId]
  );

  const renameCustomColumn = useCallback(
    async (key: string, newName: string) => {
      return await patchCustomColumn(key, { name: newName.trim() });
    },
    [patchCustomColumn]
  );

  const deactivateCustomColumn = useCallback(
    async (key: string) => {
      if (!smbId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await smbsApi.deleteCustomColumn(smbId, key);
        setCustomColumns(res.columns || []);
        return res.columns;
      } catch (err: any) {
        console.error('Failed to deactivate custom column:', err);
        setError(err.message || 'Failed to deactivate custom column');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [smbId]
  );

  const addOptionToColumn = useCallback(
    async (key: string, newOptionLabel: string) => {
      const trimmed = newOptionLabel.trim();
      if (!trimmed) return;
      const targetCol = customColumns.find((c) => c.key === key);
      const currentOpts = targetCol?.options || [];
      const targetNormalized = normalizeLabel(trimmed);
      const exists = currentOpts.some((opt) => normalizeLabel(opt) === targetNormalized);
      if (exists) {
        throw new Error(`Option "${trimmed}" already exists.`);
      }

      const updatedCols = await patchCustomColumn(key, { options: [...currentOpts, trimmed] });
      const updatedCol = updatedCols?.find((c: CustomColumnDefinition) => c.key === key);
      const addedOpt = updatedCol?.options?.find((opt: string) => opt === trimmed);
      return addedOpt;
    },
    [customColumns, patchCustomColumn]
  );

  const renameOptionInColumn = useCallback(
    async (key: string, oldOption: string, newOption: string) => {
      const trimmed = newOption.trim();
      if (!trimmed || oldOption === trimmed) return;
      const targetCol = customColumns.find((c) => c.key === key);
      const currentOpts = targetCol?.options || [];
      const newOpts = currentOpts.map((opt) => (opt === oldOption ? trimmed : opt));
      return await patchCustomColumn(key, { options: newOpts });
    },
    [customColumns, patchCustomColumn]
  );

  const updateColumnOptions = useCallback(
    async (key: string, newOptions: string[]) => {
      return await patchCustomColumn(key, { options: newOptions });
    },
    [patchCustomColumn]
  );

  return {
    customColumns,
    loading,
    error,
    fetchCustomColumns,
    saveCustomColumns,
    patchCustomColumn,
    createCustomColumn,
    renameCustomColumn,
    deactivateCustomColumn,
    addOptionToColumn,
    renameOptionInColumn,
    updateColumnOptions,
  };
}
